import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { OrganizationSelectFilterPayload } from '@app/id-modal/organization-select-filter';
import { OrganizationSelectFilterComponent } from '@app/id-modal/organization-select-filter/organization-select-filter.component';
import { apiEventAll } from '@common/helper';
import { OrganizationSelectSearchModel } from '@common/interfaces';
import { isSP } from '@lib/helpers';
import {
  AnaBizTopService,
  GetCompanyAccountsStoreService,
  OrganizationSelectSearchStoreService,
  SelectCompanyAccountStoreService,
} from '@common/services';
import { SupportComponent } from '@lib/components/support-class';
import { AlertType, AnaBizLoginStatusType, ErrorType, LoginStatusType, SessionStorageName } from '@lib/interfaces';
import { AswMasterService, CommonLibService, ModalService, PageLoadingService } from '@lib/services';
import {
  BizLoginInfoAnaBizContext,
  GetCompanyAccountsResponseDataCompanyInfoCsInner,
  SelectCompanyAccountRequest,
} from 'src/sdk-authorization';
import { debounceTime, fromEvent } from 'rxjs';

/**
 * ANA Biz組織選択
 */
@Component({
  selector: 'asw-organization-select-pres',
  templateUrl: './organization-select-pres.component.html',
  styleUrls: ['./organization-select-pres.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationSelectPresComponent extends SupportComponent {
  @Input()
  set companyAccountsData(value: GetCompanyAccountsResponseDataCompanyInfoCsInner[] | undefined) {
    this._companyAccountsData = value;
    this.loadPerPageData();
  }
  /** 組織一覧データ */
  get companyAccountsData() {
    return this._companyAccountsData;
  }
  private _companyAccountsData?: GetCompanyAccountsResponseDataCompanyInfoCsInner[];

  // 端末認識処理
  public isSp = isSP();
  public isSpPre = isSP();

  /**
   * 画面サイズの変更検知
   */
  private resizeEvent = () => {
    this.isSpPre = this.isSp;
    this.isSp = isSP();
    if (this.isSpPre !== this.isSp) {
      this._changeDetectorRef.markForCheck();
    }
  };

  constructor(
    private _common: CommonLibService,
    private _modal: ModalService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _aswMasterService: AswMasterService,
    public _getCompanyAccountsStoreService: GetCompanyAccountsStoreService,
    public _selectCompanyAccountStoreService: SelectCompanyAccountStoreService,
    public _organizationSelectSearchStoreService: OrganizationSelectSearchStoreService,
    public _anaBizTopService: AnaBizTopService,
    private _pageLoadingService: PageLoadingService
  ) {
    super(_common);
  }

  /** 組織一覧フィルターデータ */
  get filterCompanyAccountsData() {
    let _filterCompanyAccountsData = this.companyAccountsData;
    const _filterCompanyAccountCode = this.filterCompanyAccountCode;
    const _filterContractSecName = this.filterContractSecName;

    // 組織IDで絞り込み
    if (_filterCompanyAccountCode) {
      _filterCompanyAccountsData = this.companyAccountsData?.filter((v) => {
        return v.companyAccountCode?.includes(_filterCompanyAccountCode);
      });
    }

    // 組織名で絞り込み
    if (_filterContractSecName) {
      _filterCompanyAccountsData = this.companyAccountsData?.filter((v) => {
        return (
          v.contractSectionName?.includes(_filterContractSecName) ||
          v.contractSectionEnglishName?.includes(_filterContractSecName)
        );
      });
    }

    return _filterCompanyAccountsData;
  }

  /** 組織名フィルター */
  get filterContractSecName() {
    return this._organizationSelectSearchStoreService.organizationSelectSearchData.contractSecName;
  }

  /** 組織IDフィルター */
  get filterCompanyAccountCode() {
    return this._organizationSelectSearchStoreService.organizationSelectSearchData.companyAccountCode;
  }

  /** 総合データ数 */
  get totalItems() {
    return this.filterCompanyAccountsData?.length ?? 0;
  }

  /** 最大ページ数 */
  get maxPage() {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  /** ページ番号 */
  get pageNumbers() {
    let start = Math.max(this.currentPage - 2, 1);
    let end = Math.min(start + this.pagesToShow - 1, this.maxPage);

    if (end - start < this.pagesToShow - 1) {
      start = Math.max(end - this.pagesToShow + 1, 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  /** 1ページあたりの最初の項目数 */
  get firstItemsNumber() {
    return this.itemsPerPage * (this.currentPage - 1) + 1;
  }

  /** 1ページあたりの最後の項目数 */
  get lastItemsNumber() {
    return Math.min(this.itemsPerPage * this.currentPage, this.totalItems);
  }

  /** 表示数 */
  get displayNumber() {
    return `Page ${this.firstItemsNumber} ~ ${this.lastItemsNumber} / ${this.totalItems}`;
  }

  /** 言語情報 */
  aswLangCode = this._common.aswContextStoreService.aswContextData.lang;

  /** 1ページのあたりのデータ */
  perPageData?: GetCompanyAccountsResponseDataCompanyInfoCsInner[];

  /** 現在のページ番号 */
  currentPage = 1;

  /** 1ページあたりのアイテム数 */
  itemsPerPage!: number;

  /** 表示するページ番号の数 */
  pagesToShow!: number;

  /** 契約状態・静的文言キーマップ */
  contractStatusMsg = {
    underContract: 'label.contactInPlace',
    creditSuspendedByJcb: 'label.usageSuspendedByJcb',
    ticketingSuspendedByAna: 'label.usageSuspendedByAna',
    cancelled: 'label.contactCanceled',
  };

  /**
   * 1ページあたりのデータの更新処理
   */
  loadPerPageData() {
    this.perPageData = this.filterCompanyAccountsData?.slice(
      (this.currentPage - 1) * this.itemsPerPage,
      this.currentPage * this.itemsPerPage
    );
    this._changeDetectorRef.markForCheck();
  }

  /**
   * ページ移動処理
   */
  goToPage(n: number) {
    this.currentPage = n;
    this.loadPerPageData();
  }

  /**
   * 前へボタン
   */
  onNext() {
    this.goToPage(Math.min(this.currentPage + 1, this.maxPage));
  }

  /**
   * 次へボタン
   */
  onPrev() {
    this.goToPage(Math.max(this.currentPage - 1, 1));
  }

  /**
   * 組織選択フィルタモーダル表示ボタン
   */
  clickFilterButton() {
    const part = this._modal.defaultBlockPart(OrganizationSelectFilterComponent, 'OrganizationSelectFilter');
    part.payload = {
      updateEvent: () => {
        // 画面更新
        this._changeDetectorRef.detectChanges();
        this.goToPage(1);

        // 0件の場合
        if (this.filterCompanyAccountsData?.length === 0) {
          this._common.alertMessageStoreService.setAlertWarningMessage({
            contentHtml: 'E0877',
            errorMessageId: 'E0877',
            isCloseEnable: false,
            alertType: AlertType.WARNING,
          });
        } else {
          this._common.alertMessageStoreService.resetAlertMessage();
        }
      },
    } as OrganizationSelectFilterPayload | null;
    this._modal.showSubModal(part);
  }

  /**
   * 組織一覧押下
   * @param
   */
  clickListItem(companyAccountCode: string, contractSectionName?: string) {
    // 組織一覧検索条件storeをセット
    const model: OrganizationSelectSearchModel = {
      contractSecName: contractSectionName,
      companyAccountCode: companyAccountCode,
    };
    this._organizationSelectSearchStoreService.setOrganizationSelectSearch(model);

    // 組織選択API実行
    const selectCompanyAccountRequestParam: SelectCompanyAccountRequest = {
      companyAccountCode: companyAccountCode,
    };
    this.callSelectComapnyAccountApi(selectCompanyAccountRequestParam);
  }

  /**
   * ANA Biz 組織選択API実行
   * @param requeset 組織選択APIのリクエストパラメータ
   */
  callSelectComapnyAccountApi(request: SelectCompanyAccountRequest) {
    this._pageLoadingService.startLoading();
    apiEventAll(
      () => {
        this._selectCompanyAccountStoreService.setSelectCompanyAccountFromApi(request);
      },
      this._selectCompanyAccountStoreService.selectCompanyAccount$(),
      (response) => {
        this._pageLoadingService.endLoading();
        // anaBizContext、aswContextを更新する
        this._common.anaBizContextStoreService.updateAnaBizContext({
          ...response.data.anaBizContext,
        });

        const anaBizLoginStatus = response.data.aswContext?.anaBizLoginStatus;
        const loginStatus = response.data.aswContext?.loginStatus;
        this._common.aswContextStoreService.updateAswContext({
          loginStatus: loginStatus as LoginStatusType | undefined,
          anaBizLoginStatus: anaBizLoginStatus as AnaBizLoginStatusType | undefined,
        });

        const accessToken = this._common.loadSessionStorage(SessionStorageName.ACCESS_TOKEN);
        const anaBizContext: BizLoginInfoAnaBizContext = response.data?.anaBizContext ?? {};
        const encryptedAnaBizAccessToken = anaBizContext?.encryption?.anaBizAccessToken || '';

        // リクエストヘッダとして送付したaccessTokenとAPIレスポンスのanaBizAccessTokenをlocalStorageに保存する
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('encryptedAnaBizAccessToken', encryptedAnaBizAccessToken);

        // ANA Biz TOPへ遷移を行う
        this._anaBizTopService.sendAnaBizTop(anaBizContext);
      },
      (error) => {
        this._pageLoadingService.endLoading();
        const errorCodeMap: { [key: string]: string } = {
          EAPZ000028: 'E1069', // ログインステータスが不正
        };

        const apiCode = this._common.apiError?.errors?.[0]?.code ?? '';
        const code = errorCodeMap[apiCode];

        if (code) {
          this._common.errorsHandlerService.setNotRetryableError({
            errorType: ErrorType.BUSINESS_LOGIC,
            apiErrorCode: apiCode,
            errorMsgId: code,
          });
        } else {
          // システムエラー
          this._common.errorsHandlerService.setNotRetryableError({
            errorType: ErrorType.SYSTEM,
            apiErrorCode: apiCode,
          });
        }

        this._selectCompanyAccountStoreService.resetSelectCompanyAccount();
      }
    );
  }

  init(): void {
    // 設定ファイルから取得
    this.itemsPerPage = Number(
      this._aswMasterService.getMPropertyByKey('otherApplicationSettings', 'organizationSelect.itemsPerPage')
    );
    this.pagesToShow = Number(
      this._aswMasterService.getMPropertyByKey('otherApplicationSettings', 'organizationSelect.pagesToShow')
    );

    this.subscribeService('FilterResize', fromEvent(window, 'resize').pipe(debounceTime(100)), this.resizeEvent);
  }
  reload(): void {}
  destroy(): void {}
}
